import countryData from 'src/assets/json/countries.json'
import countryStateData from 'src/assets/json/countries_states.json'
import type { TravelerHomeCountry } from 'src/types/market.type'

const US_STATE_CODES: Record<string, string> = {
   'Alabama': 'AL',
   'Alaska': 'AK',
   'American Samoa': 'AS',
   'Arizona': 'AZ',
   'Arkansas': 'AR',
   'California': 'CA',
   'Colorado': 'CO',
   'Connecticut': 'CT',
   'Delaware': 'DE',
   'District of Columbia': 'DC',
   'Florida': 'FL',
   'Georgia': 'GA',
   'Guam': 'GU',
   'Hawaii': 'HI',
   'Idaho': 'ID',
   'Illinois': 'IL',
   'Indiana': 'IN',
   'Iowa': 'IA',
   'Kansas': 'KS',
   'Kentucky': 'KY',
   'Louisiana': 'LA',
   'Maine': 'ME',
   'Maryland': 'MD',
   'Massachusetts': 'MA',
   'Michigan': 'MI',
   'Minnesota': 'MN',
   'Mississippi': 'MS',
   'Missouri': 'MO',
   'Montana': 'MT',
   'Nebraska': 'NE',
   'Nevada': 'NV',
   'New Hampshire': 'NH',
   'New Jersey': 'NJ',
   'New Mexico': 'NM',
   'New York': 'NY',
   'North Carolina': 'NC',
   'North Dakota': 'ND',
   'Northern Mariana Islands': 'MP',
   'Ohio': 'OH',
   'Oklahoma': 'OK',
   'Oregon': 'OR',
   'Pennsylvania': 'PA',
   'Puerto Rico': 'PR',
   'Rhode Island': 'RI',
   'South Carolina': 'SC',
   'South Dakota': 'SD',
   'Tennessee': 'TN',
   'Texas': 'TX',
   'United States Virgin Islands': 'VI',
   'Utah': 'UT',
   'Vermont': 'VT',
   'Virginia': 'VA',
   'Washington': 'WA',
   'West Virginia': 'WV',
   'Wisconsin': 'WI',
   'Wyoming': 'WY',
};

// ─── NPT-2008 supported-country allowlist ─────────────────────────────────────────────────────
// The one place the web app names the countries its travelers may LIVE in (billing and profile
// addresses, the phone picker, market defaults) and the defaults it falls back to. Not
// destinations: a traveler can book a hotel in any country the supplier covers. Market-driven
// defaults (useMarket) replace DEFAULT_ADDRESS_COUNTRY / DEFAULT_PHONE_COUNTRY at the call sites
// that read them; the list itself stays fixed (it mirrors npt-api's
// SUPPORTED_TRAVELER_HOME_COUNTRIES, which the site payload reports as travelerHomeCountries).

const SUPPORTED_TRAVELER_HOME_COUNTRIES: readonly TravelerHomeCountry[] = ['US', 'CA', 'MX'];

/** Address country preselected when nothing else says otherwise. */
const DEFAULT_ADDRESS_COUNTRY: TravelerHomeCountry = 'US';

/** Phone picker default. +1 covers Canada too, so US is right for both. */
const DEFAULT_PHONE_COUNTRY: TravelerHomeCountry = 'US';

/**
 * A saved card or profile that predates the country field holds no country; everything stored
 * before NPT-2008 was a US address (checkout could not take any other), so "none" reads as US.
 * Deliberately NOT DEFAULT_ADDRESS_COUNTRY: a Canadian market default must not relabel an old
 * Texas card as Canadian.
 */
const LEGACY_ADDRESS_COUNTRY: TravelerHomeCountry = 'US';

const isTravelerHomeCountry = (value: unknown): value is TravelerHomeCountry =>
   typeof value === 'string' && (SUPPORTED_TRAVELER_HOME_COUNTRIES as readonly string[]).includes(value);

/** 'ca', ' CA ' → 'CA'; anything outside the allowlist (or empty) → undefined. */
const toTravelerHomeCountry = (value?: string | null): TravelerHomeCountry | undefined => {
   const upper = value?.trim().toUpperCase();
   return isTravelerHomeCountry(upper) ? upper : undefined;
};

// Full names older profile/card rows may carry, before the country was stored as a code.
// Mirrors npt-api's toTravelerHomeCountryLoose (src/utils/market.util.ts).
const COUNTRY_NAME_ALIASES: Record<string, TravelerHomeCountry> = {
   'USA': 'US',
   'UNITED STATES': 'US',
   'UNITED STATES OF AMERICA': 'US',
   'CANADA': 'CA',
   'MEXICO': 'MX',
   'MÉXICO': 'MX',
};

/**
 * Like toTravelerHomeCountry, but also reads the full names stored values may carry ("Mexico",
 * "United States", "USA"). For values read back from a profile or a saved card; never for a value
 * the user is picking (the select only offers codes).
 */
const toTravelerHomeCountryLoose = (value?: string | null): TravelerHomeCountry | undefined =>
   toTravelerHomeCountry(value) ?? (value ? COUNTRY_NAME_ALIASES[value.trim().replace(/\s+/g, ' ').toUpperCase()] : undefined);

// Mapbox country filter per address country. US keeps its territories, matching US_STATE_CODES.
const ADDRESS_COUNTRY_FILTER: Record<TravelerHomeCountry, string> = {
   US: 'us,pr,vi,gu,as,mp',
   CA: 'ca',
   MX: 'mx',
};

const getAddressCountryFilter = (country: TravelerHomeCountry) => ADDRESS_COUNTRY_FILTER[country];

// ISO 3166-2:CA subdivision codes (also what Mapbox returns as region_code).
const CA_PROVINCE_CODES: Record<string, string> = {
   'Alberta': 'AB',
   'British Columbia': 'BC',
   'Manitoba': 'MB',
   'New Brunswick': 'NB',
   'Newfoundland and Labrador': 'NL',
   'Northwest Territories': 'NT',
   'Nova Scotia': 'NS',
   'Nunavut': 'NU',
   'Ontario': 'ON',
   'Prince Edward Island': 'PE',
   'Quebec': 'QC',
   'Saskatchewan': 'SK',
   'Yukon': 'YT',
};

// ISO 3166-2:MX subdivision codes. Mapbox returns exactly these as region_code (NLE, JAL, CMX…),
// verified against the Search Box API on 2026-10-11. Keyed by the names in countries_states.json.
const MX_STATE_CODES: Record<string, string> = {
   'Aguascalientes': 'AGU',
   'Baja California': 'BCN',
   'Baja California Sur': 'BCS',
   'Campeche': 'CAM',
   'Chiapas': 'CHP',
   'Chihuahua': 'CHH',
   'Ciudad de México': 'CMX',
   'Coahuila de Zaragoza': 'COA',
   'Colima': 'COL',
   'Durango': 'DUR',
   'Estado de México': 'MEX',
   'Guanajuato': 'GUA',
   'Guerrero': 'GRO',
   'Hidalgo': 'HID',
   'Jalisco': 'JAL',
   'Michoacán de Ocampo': 'MIC',
   'Morelos': 'MOR',
   'Nayarit': 'NAY',
   'Nuevo León': 'NLE',
   'Oaxaca': 'OAX',
   'Puebla': 'PUE',
   'Querétaro': 'QUE',
   'Quintana Roo': 'ROO',
   'San Luis Potosí': 'SLP',
   'Sinaloa': 'SIN',
   'Sonora': 'SON',
   'Tabasco': 'TAB',
   'Tamaulipas': 'TAM',
   'Tlaxcala': 'TLA',
   'Veracruz de Ignacio de la Llave': 'VER',
   'Yucatán': 'YUC',
   'Zacatecas': 'ZAC',
};

/**
 * Other ways a Mexican state is written: the short names Mapbox uses ("Coahuila", "Mexico",
 * "Veracruz"…), English names, and the everyday abbreviations people type or autofill holds
 * ("N.L.", "CDMX", "Edomex"). Matched after stripping accents, dots and spaces. Keyed by ISO code.
 */
const MX_STATE_ALIASES: Record<string, string[]> = {
   AGU: ['AGS'],
   BCN: ['BC'],
   CHP: ['CHIS'],
   CHH: ['CHIH'],
   CMX: ['CDMX', 'DF', 'Distrito Federal', 'Mexico City'],
   COA: ['Coahuila', 'COAH'],
   DUR: ['DGO'],
   MEX: ['Mexico', 'State of Mexico', 'Edomex', 'EM'],
   GUA: ['GTO'],
   HID: ['HGO'],
   MIC: ['Michoacan', 'MICH'],
   NLE: ['NL'],
   QUE: ['QRO', 'Queretaro de Arteaga'],
   ROO: ['QR', 'QROO'],
   TAM: ['TAMPS'],
   TLA: ['TLAX'],
   VER: ['Veracruz'],
};

const SUBDIVISION_CODES: Record<TravelerHomeCountry, Record<string, string>> = {
   US: US_STATE_CODES,
   CA: CA_PROVINCE_CODES,
   MX: MX_STATE_CODES,
};

type StateOption = { label: string; value: string; code: string | undefined; aliases?: string[] };

const getCountryOptions = () => {
   return countryData.map(country => ({
      label: country.name,
      value: country.iso2.toLowerCase()
   }))
}

const getStateOptions = ({ name, iso2 }: { name?: string, iso2?: string }): StateOption[] => {
   if (!name && !iso2) return []

   const countries = getCountryOptions()
   let selectedCountry = null;
   if (iso2) {
      selectedCountry = countries.find(country => country.value === iso2.toLowerCase());
   } else if (name) {
      selectedCountry = countries.find(country => country.label === name);
   }

   if (selectedCountry) {
      const supported = toTravelerHomeCountry(selectedCountry.value);
      const codes = supported ? SUBDIVISION_CODES[supported] : undefined;
      return countryStateData
         .find(countryState => countryState.name === selectedCountry.label)
         ?.states?.map(state => {
            const code = codes?.[state];
            const aliases = supported === 'MX' && code ? MX_STATE_ALIASES[code] : undefined;
            return {
               label: state,
               value: state,
               code,
               ...(aliases ? { aliases } : {}),
            };
         }) || [];
   }

   return []
}

/** "Nuevo León", "N.L.", " nuevo leon " → "NUEVOLEON" / "NL": accents, dots and spaces don't count. */
const foldForMatch = (value: string) =>
   value
      .normalize('NFD')
      .replace(/[̀-ͯ]/g, '')
      .replace(/[\s.\-_]/g, '')
      .toUpperCase();

/**
 * The State/Province option an address part names, for any supported country: by subdivision code
 * first (Mapbox's region_code, "ON" / "NLE", with or without its "CA-" / "MX-" prefix), then by
 * name, then by alias — accents, case, dots and spaces ignored. Undefined when nothing matches.
 */
const findStateOption = (country: TravelerHomeCountry, { name, code }: { name?: string | null; code?: string | null }) => {
   const options = getStateOptions({ iso2: country });
   const wantedCode = code ? foldForMatch(code.replace(new RegExp(`^${country}-`, 'i'), '')) : '';
   if (wantedCode) {
      const byCode = options.find(option => option.code && foldForMatch(option.code) === wantedCode);
      if (byCode) return byCode;
   }
   const wantedName = name ? foldForMatch(name) : '';
   if (!wantedName) return undefined;
   return (
      options.find(option => foldForMatch(option.label) === wantedName) ??
      options.find(option => option.code && foldForMatch(option.code) === wantedName) ??
      options.find(option => option.aliases?.some(alias => foldForMatch(alias) === wantedName))
   );
};

// ─── Postal codes ─────────────────────────────────────────────────────────────────────────────
// Canada Post never uses D, F, I, O, Q or U, and never W or Z as the first letter.
const CA_POSTAL_PATTERN = /^[ABCEGHJ-NPRSTVXY]\d[ABCEGHJ-NPRSTV-Z] ?\d[ABCEGHJ-NPRSTV-Z]\d$/;

const POSTAL_CODE_PATTERNS: Record<TravelerHomeCountry, RegExp> = {
   US: /^\d{5}(-\d{4})?$/,
   CA: CA_POSTAL_PATTERN,
   MX: /^\d{5}$/,
};

/**
 * The postal code as it should be stored and sent: trimmed, upper case, and for Canada with its
 * single middle space ("m5v2t6" → "M5V 2T6"); a 9-digit US ZIP gets its dash. Anything that does
 * not look like a code for the country comes back trimmed but otherwise as typed, so validation
 * can still name the problem.
 */
const normalizePostalCode = (country: TravelerHomeCountry, value?: string | null) => {
   const trimmed = (value ?? '').trim().toUpperCase();
   if (country === 'CA') {
      const compact = trimmed.replace(/\s+/g, '');
      return /^[A-Z]\d[A-Z]\d[A-Z]\d$/.test(compact) ? `${compact.slice(0, 3)} ${compact.slice(3)}` : trimmed;
   }
   if (country === 'US' && /^\d{9}$/.test(trimmed)) return `${trimmed.slice(0, 5)}-${trimmed.slice(5)}`;
   return trimmed;
};

/**
 * NPT-2055 — nationalities offered first in the residency pickers: the market's default country,
 * then the rest of the allowlist. Lower case, matching src/utils/countries.ts codes.
 */
const recommendedNationalityCodes = (first: TravelerHomeCountry = DEFAULT_ADDRESS_COUNTRY): string[] =>
   [first, ...SUPPORTED_TRAVELER_HOME_COUNTRIES.filter(code => code !== first)].map(code => code.toLowerCase());

/**
 * NPT-2055 — ISO-2 country (upper case) of a picked Mapbox place's `context`: the Search Box
 * object shape ({ country: { country_code } }) or the v5 geocoder's array ({ id: 'country.…',
 * short_code }). Undefined when the context names no country.
 */
const countryCodeFromPlaceContext = (context: unknown): string | undefined => {
   if (Array.isArray(context)) {
      const country = context.find((item: { id?: string }) => typeof item?.id === 'string' && item.id.startsWith('country'));
      const code = (country as { short_code?: string } | undefined)?.short_code;
      return code ? code.toUpperCase() : undefined;
   }
   const code = (context as { country?: { country_code?: string } } | null | undefined)?.country?.country_code;
   return code ? code.toUpperCase() : undefined;
};

const isValidPostalCode = (country: TravelerHomeCountry, value?: string | null) =>
   POSTAL_CODE_PATTERNS[country].test(normalizePostalCode(country, value));

/**
 * The country of an address saved before addresses stored one: US (LEGACY_ADDRESS_COUNTRY), unless
 * its postal code is plainly Canadian. Canadians who booked before NPT-2008 typed "M5V 2T6" into
 * the US-only form; reading that row as US would fail its own ZIP check on every later save, and
 * switching the country to fix it clears the whole address.
 */
const legacyAddressCountry = (postalCode?: string | null): TravelerHomeCountry =>
   postalCode && isValidPostalCode('CA', postalCode) ? 'CA' : LEGACY_ADDRESS_COUNTRY;

export {
   getCountryOptions,
   getStateOptions,
   findStateOption,
   SUPPORTED_TRAVELER_HOME_COUNTRIES,
   DEFAULT_ADDRESS_COUNTRY,
   DEFAULT_PHONE_COUNTRY,
   LEGACY_ADDRESS_COUNTRY,
   legacyAddressCountry,
   isTravelerHomeCountry,
   toTravelerHomeCountry,
   toTravelerHomeCountryLoose,
   getAddressCountryFilter,
   CA_PROVINCE_CODES,
   MX_STATE_CODES,
   normalizePostalCode,
   isValidPostalCode,
   recommendedNationalityCodes,
   countryCodeFromPlaceContext,
}
export type { StateOption }
